// Lazy OCR runtime (Tesseract.js v5). Loaded from CDN via a <script> tag so it
// runs as the `window.Tesseract` global: webpack 4 cannot bundle the v5 source,
// and mixing a bundled v2 API with v5 worker/core assets produces garbage
// output. The v5 API is createWorker(langs, oem, options) — async, auto-loads
// the language and initialises; worker/core/lang default to the matching CDN.
const TESSERACT_CDN =
    'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js'

let scriptPromise = null
let workerPromise = null
let busy = false

function loadTesseract() {
    if (typeof window !== 'undefined' && window.Tesseract)
        return Promise.resolve(window.Tesseract)
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const s = document.createElement('script')
            s.src = TESSERACT_CDN
            s.async = true
            s.onload = () => resolve(window.Tesseract)
            s.onerror = () => {
                scriptPromise = null
                reject(new Error('Failed to load Tesseract.js'))
            }
            document.head.appendChild(s)
        })
    }
    return scriptPromise
}

async function getWorker() {
    if (!workerPromise) {
        workerPromise = (async () => {
            const Tesseract = await loadTesseract()
            // v5: (languages, oem=1 LSTM, options). Auto loads + initialises.
            return Tesseract.createWorker('eng', 1)
        })().catch((e) => {
            workerPromise = null
            throw e
        })
    }
    return workerPromise
}

// Recognise text in a canvas/image. Returns { text, confidence } on success
// (confidence 0-100 from the LSTM engine) or null when skipped/failed. Skips
// overlapping calls (OCR is slow — one frame at a time).
export async function recognizeText(source) {
    if (busy || !source) return null
    busy = true
    try {
        const worker = await getWorker()
        const { data } = await worker.recognize(source)
        return {
            text: (data && data.text ? data.text : '').trim(),
            confidence:
                data && typeof data.confidence === 'number'
                    ? data.confidence
                    : 0,
        }
    } catch (e) {
        return null
    } finally {
        busy = false
    }
}

// Below this the LSTM reading is mostly noise. Kept low so the panel stays
// responsive/live; the display clears when nothing readable is in frame.
export const OCR_MIN_CONFIDENCE = 50

// Light OCR preprocessing: grayscale only. The v5 LSTM engine reads colour/
// scene text well on its own, and hard binarization was mangling frames, so we
// keep this to a gentle grayscale (mild denoise) rather than a threshold.
export function preprocessCanvasForOcr(canvas) {
    try {
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        const { width: W, height: H } = canvas
        const img = ctx.getImageData(0, 0, W, H)
        const d = img.data
        for (let i = 0; i < d.length; i += 4) {
            const g =
                ((d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000) | 0
            d[i] = d[i + 1] = d[i + 2] = g
        }
        ctx.putImageData(img, 0, 0)
    } catch (e) {
        /* best-effort */
    }
}

export async function terminateOcr() {
    if (!workerPromise) return
    try {
        const worker = await workerPromise
        await worker.terminate()
    } catch (e) {
        /* ignore */
    }
    workerPromise = null
}
